import { css } from '@emotion/react';

import * as React from 'react';

import config from 'js/app/config';
import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Typography2, useTheme } from '@coursera/cds-core';
import { LockIcon } from '@coursera/cds-icons';

import DegreesMartechExperiments from 'bundles/epic/clients/DegreesMartech';

import _t from 'i18n!nls/xddp';

type Props = {
  partnerName: string;
  consentMessage: string;
};

const EoiTosNotice = ({ partnerName, consentMessage }: Props) => {
  const theme = useTheme();
  const shouldShowRequiredEoiUpenn = DegreesMartechExperiments.get('shouldShowRequiredEoiUpenn');

  const getMessage = (): string => {
    if (consentMessage) {
      return consentMessage;
    } else if (shouldShowRequiredEoiUpenn) {
      return _t(
        `<span aria-hidden="true">Secure form. By submitting, I agree that Coursera will share the above information with the University of Pennsylvania and,
        if I apply, the University will share information about the status of my application with Coursera.
        I also specifically consent to the University of Pennsylvania, or its authorized marketing affiliate, contacting me
        to follow up on my submission and answer any questions I may have. Furthermore, while interacting with the 
        Coursera website, I agree to abide by </span> {tosLink}.`
      );
    } else {
      return _t(
        `<span aria-hidden="true">Secure form. By submitting, I agree that Coursera will share the above information with {partnerName}. 
        I also specifically consent to Coursera, {partnerName}, or an authorized marketing affiliate to contact me 
        to follow up on my submission and answer any questions I may have. Furthermore, while interacting with the 
        Coursera website, I agree to abide by </span> {tosLink}.`
      );
    }
  };

  const getLabel = (): string => {
    if (shouldShowRequiredEoiUpenn) {
      return _t(
        `Secure form. By submitting, I agree that Coursera will share the above information with the University of Pennsylvania and, 
        if I apply, the University will share information about the status of my application with Coursera. 
        I also specifically consent to the University of Pennsylvania, or its authorized marketing affiliate, contacting me 
        to follow up on my submission and answer any questions I may have. Furthermore, while interacting with the 
        Coursera website, I agree to abide by Coursera's Terms of Use.`
      );
    } else {
      return _t(
        `Secure form. By submitting, I agree that Coursera will share the above information with #{partnerName}.
        I also specifically consent to Coursera, #{partnerName}, or an authorized marketing affiliate to contact me 
        to follow up on my submission and answer any questions I may have. Furthermore, while interacting with the 
        Coursera website, I agree to abide by Coursera's Terms of Service`,
        { partnerName }
      );
    }
  };

  return (
    <div className="rc-EoiTosNotice horizontal-box">
      <LockIcon
        size="large"
        css={css`
          margin: ${theme.spacing(0, 8, 0, 0)};
          width: 40px;
        `}
      />
      <Typography2 component="p" variant="bodySecondary" className="m-r-1" color="supportText">
        <FormattedHTMLMessage
          message={getMessage()}
          partnerName={partnerName}
          tosLink={
            <a aria-label={getLabel()} href={`${config.url.base}about/terms`}>
              {_t("Coursera's Terms of Use")}
            </a>
          }
        />
      </Typography2>
    </div>
  );
};

export default EoiTosNotice;
